import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getProducts, deleteProduct } from '../../actions/product';
import { Plus, Search, Edit, Trash2, ShoppingCart, Tag, AlertTriangle, Package2, Eye, Droplets } from 'lucide-react';

const Products = ({ getProducts, deleteProduct, products, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        document.title = 'Products';
        getProducts();
    }, []);

    useEffect(() => {
        if (products) {
            let filtered = products.filter(product =>
                product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.tagline?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.productType?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (typeFilter !== 'all') {
                filtered = filtered.filter(product =>
                    product.productType?.toLowerCase() === typeFilter.toLowerCase()
                );
            }

            setFilteredProducts(filtered);
        }
    }, [products, searchTerm, typeFilter]);

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (productToDelete) {
            deleteProduct(productToDelete._id);
            setShowDeleteModal(false);
            setProductToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setProductToDelete(null);
    };

    const getTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'water': return 'info';
            case 'membership': return 'success';
            case 'accessory': return 'warning';
            case 'refundabledeposit': return 'secondary';
            default: return 'primary';
        }
    };

    const getValidTypes = () => {
        return ['water', 'membership', 'accessory', 'refundableDeposit'];
    };

    const getUniqueTypes = () => {
        if (!products) return [];
        const types = products.map(p => p.productType).filter(Boolean);
        return [...new Set(types)];
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <ShoppingCart size={32} className="me-3 text-primary" />
                                    Products
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage your product catalog and inventory
                                </p>
                            </div>
                            <Link
                                to="/inventory/v1.2/products/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Add New Product
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{products?.length || 0}</div>
                                        <div className="small">Total Products</div>
                                    </div>
                                    <Package2 size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{getUniqueTypes().length}</div>
                                        <div className="small">Product Types</div>
                                    </div>
                                    <Tag size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">
                                            {products?.filter(p => p.productType === 'water').length || 0}
                                        </div>
                                        <div className="small">Water Products</div>
                                    </div>
                                    <Droplets size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{filteredProducts?.length || 0}</div>
                                        <div className="small">Filtered Results</div>
                                    </div>
                                    <Search size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search products by name, brand, tagline, description, or type..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    {getValidTypes().map(type => (
                                        <option key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading products...</p>
                    </div>
                ) : filteredProducts && filteredProducts.length > 0 ? (
                    <div className="row">
                        {filteredProducts.map((product) => (
                            <div key={product._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div
                                    className="card shadow-sm border-0 h-100"
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    {product.name || product.brand || 'Unnamed Product'}
                                                </h5>
                                                {product.productType && (
                                                    <span
                                                        className={`badge bg-${getTypeColor(product.productType)} mb-2`}
                                                        style={{ fontSize: '0.75rem' }}
                                                    >
                                                        {product.productType}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="btn-group">
                                                <Link
                                                    to={`/inventory/v1.2/products/${product._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Product"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(product)}
                                                    title="Delete Product"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-body pt-2">
                                        {/* Product Image */}
                                        {product.image && (
                                            <div className="text-center mb-3">
                                                <div
                                                    className="d-flex justify-content-center align-items-center bg-light rounded"
                                                    style={{ height: '150px', overflow: 'hidden' }}
                                                >
                                                    <img
                                                        src={`${process.env.REACT_APP_SERVER_PUBLIC_PATH}${process.env.REACT_APP_IMAGE_PATH_PRODUCT}/${product.image}`}
                                                        alt={product.brand || 'Product'}
                                                        className="img-fluid rounded"
                                                        style={{
                                                            maxHeight: '140px',
                                                            maxWidth: '100%',
                                                            objectFit: 'contain'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Product Details */}
                                        <div className="mb-3">
                                            {product.tagline && (
                                                <p className="text-muted small mb-2 fst-italic">
                                                    "{product.tagline}"
                                                </p>
                                            )}

                                            {product.description && (
                                                <p className="text-muted small mb-2">
                                                    {product.description.length > 80
                                                        ? `${product.description.substring(0, 80)}...`
                                                        : product.description
                                                    }
                                                </p>
                                            )}
                                        </div>

                                        {/* Additional Info */}
                                        <div className="row g-2">
                                            <div className="col-6">
                                                <div className="bg-light rounded p-2 text-center">
                                                    <div className="small text-muted">Brand</div>
                                                    <div className="fw-bold text-dark small">
                                                        {product.brand || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="bg-light rounded p-2 text-center">
                                                    <div className="small text-muted">Type</div>
                                                    <div className="fw-bold text-dark small">
                                                        {product.productType || 'Standard'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-footer bg-light border-top-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link
                                                to={`/inventory/v1.2/products/${product._id}`}
                                                className="btn btn-primary btn-sm"
                                            >
                                                <Edit size={14} className="me-1" />
                                                Manage
                                            </Link>
                                            <small className="text-muted">
                                                ID: {product._id?.slice(-6)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <ShoppingCart size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted mb-3">
                            {searchTerm || typeFilter !== 'all' ? 'No products found' : 'No products created yet'}
                        </h4>
                        <p className="text-muted mb-4">
                            {searchTerm || typeFilter !== 'all'
                                ? 'No products match your search criteria. Try adjusting your filters.'
                                : 'Get started by adding your first product to the catalog.'
                            }
                        </p>
                        {(!searchTerm && typeFilter === 'all') ? (
                            <Link
                                to="/inventory/v1.2/products/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Add Your First Product
                            </Link>
                        ) : (
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    setSearchTerm('');
                                    setTypeFilter('all');
                                }}
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title d-flex align-items-center text-danger">
                                        <AlertTriangle size={24} className="me-2" />
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={cancelDelete}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <Trash2 size={48} className="text-danger" />
                                        </div>
                                        <h6 className="mb-3">
                                            Delete Product "{productToDelete?.brand || productToDelete?.name}"?
                                        </h6>
                                        <p className="text-muted mb-4">
                                            This action cannot be undone. The product and all its data
                                            will be permanently deleted.
                                        </p>

                                        {/* Show product details in modal */}
                                                                                 {productToDelete && (
                                            <div className="alert alert-info">
                                                <small>
                                                    <strong>Product Details:</strong><br />
                                                    Brand: {productToDelete.brand || 'N/A'}<br />
                                                    {productToDelete.name && (
                                                        <>Name: {productToDelete.name}<br /></>
                                                    )}
                                                    {productToDelete.tagline && (
                                                        <>Tagline: {productToDelete.tagline}<br /></>
                                                    )}
                                                    Type: {productToDelete.type || 'Standard'}
                                                </small>
                                            </div>
                                        )}

                                        <div className="alert alert-warning d-flex align-items-center">
                                            <AlertTriangle size={20} className="me-2" />
                                            <small>
                                                <strong>Warning:</strong> This will permanently delete the product and all its data.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={cancelDelete}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        <Trash2 size={16} className="me-2" />
                                        Delete Product
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

Products.propTypes = {
    products: PropTypes.array,
    getProducts: PropTypes.func.isRequired,
    deleteProduct: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

const mapStateToProps = state => ({
    products: state.product.products,
    loading: state.product.loading
});

export default connect(mapStateToProps, { getProducts, deleteProduct })(Products);

